import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sparkles } from 'lucide-react';
import { useAudience } from '@/contexts/AudienceContext';
import { trackTryDiscoveryClick } from '@/lib/gtag';

/**
 * High-impact strip between trust and problem — student mode only.
 */
const TryDemoInviteBand = () => {
  const { isSchool } = useAudience();
  if (isSchool) return null;

  return (
    <section aria-labelledby="try-demo-invite-heading" className="relative overflow-hidden py-14 sm:py-16">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-teal-900" />
      <div className="pointer-events-none absolute -left-20 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-teal-500/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 top-0 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

      <div className="container relative z-10 mx-auto px-6">
        <div className="mx-auto flex max-w-5xl flex-col gap-8 rounded-3xl border border-white/15 bg-white/[0.07] p-8 shadow-2xl shadow-black/30 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between sm:gap-10 sm:p-10 lg:p-12">
          <div className="min-w-0 flex-1 space-y-4">
            <div className="inline-flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-teal-400/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-teal-200">
                New
              </span>
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-100/90">
                Free · No sign-up
              </span>
              <Sparkles className="hidden h-4 w-4 text-amber-300 sm:inline" aria-hidden />
            </div>
            <h2
              id="try-demo-invite-heading"
              className="font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.35rem]"
            >
              <span className="bg-gradient-to-r from-teal-300 via-cyan-200 to-violet-200 bg-clip-text text-transparent">
                See how Career DNA reads your style—in about a minute
              </span>
            </h2>
            <p className="max-w-xl text-base leading-relaxed text-blue-100/95 sm:text-lg">
              Six quick choices, no sign-up. A gentle snapshot of how you tend to think and what kinds of paths might
              suit you.
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-stretch gap-3 sm:items-end">
            <Link
              to="/try"
              onClick={() => trackTryDiscoveryClick('invite_band')}
              className="group relative inline-flex min-h-[3.5rem] items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-teal-400 via-teal-500 to-blue-500 px-8 py-4 text-lg font-bold text-slate-950 shadow-lg shadow-teal-900/40 transition hover:scale-[1.02] hover:shadow-xl hover:shadow-teal-800/50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-300/60"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 opacity-0 transition duration-500 group-hover:translate-x-full group-hover:opacity-100" />
              <Play className="relative h-6 w-6 fill-slate-950 text-slate-950" aria-hidden />
              <span className="relative">See my preview</span>
              <ArrowRight className="relative h-5 w-5 transition group-hover:translate-x-0.5" aria-hidden />
            </Link>
            <p className="text-center text-xs text-slate-400 sm:text-right">Takes about a minute · works on phone</p>
            <Link
              to="/start"
              className="text-center text-sm font-medium text-teal-200/95 underline-offset-4 hover:text-white hover:underline sm:text-right"
            >
              Take The Full Career DNA Assessment →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TryDemoInviteBand;
